/**
 * Metric vocabulary. Each closed set is declared once as a value list and its type derived from it, so a
 * new member cannot reach the type without also reaching the runtime check. The attribute allowlist is a
 * privacy guard, not a cardinality
 * one: Sentry handles high-cardinality attributes fine, but patient data and free text must never ride
 * along on a metric (`sendDefaultPii: false`).
 */

/**
 * The metric names the app emits. Stable, namespaced identifiers. Every one starts with `careflow.`:
 * CareFlow, CareGate and the practitioners app report into the same Sentry organisation, and CareGate has
 * a check-in of its own that means something else, so an unprefixed name merges two different things in
 * any dashboard spanning projects.
 */
export enum MetricName {
    CHECKIN_OUTCOME = 'careflow.checkin.outcome',
    CHECKIN_DURATION = 'careflow.checkin.duration',
    CARD_READ_OUTCOME = 'careflow.card_read.outcome',
    AVAILABILITY_DURATION = 'careflow.availability.duration',
    AVAILABILITY_OUTCOME = 'careflow.availability.outcome',
}

export type MetricAttributeValue = string | number | boolean;

/** Terminal outcome of a check-in or card read. `abort` is user-cancel (excluded from error-free rate). */
export const METRIC_OUTCOMES = ['success', 'failure', 'abort'] as const;
export type MetricOutcome = (typeof METRIC_OUTCOMES)[number];

/** How the check-in ran: one-pass/auto-dispatch vs. manual two-click. */
export const CHECKIN_TYPES = ['auto', 'manual'] as const;
export type CheckinType = (typeof CHECKIN_TYPES)[number];

/** Where the check-in was triggered from. */
export const CHECKIN_ENTRIES = ['cta', 'drag'] as const;
export type CheckinEntry = (typeof CHECKIN_ENTRIES)[number];

/** Which booking flow asked for availability. */
export const AVAILABILITY_MODES = ['single', 'series', 'chain'] as const;
export type AvailabilityMode = (typeof AVAILABILITY_MODES)[number];

/** The control that asked for slots. */
export const AVAILABILITY_ACTIONS = [
    'open_slots',
    'next_page',
    'previous_page',
    'jump_next_free',
    'auto_suggest',
    'next_suggestion',
    'previous_suggestion',
] as const;
export type AvailabilityAction = (typeof AVAILABILITY_ACTIONS)[number];

/** Whether the painted slots came off the wire or out of the SWR cache. */
export const METRIC_SOURCES = ['network', 'cache'] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];

// Closed set of failure reasons - a new reason is a reviewed addition here, so free text or PII can
// never be forwarded through the `reason` attribute.
export const METRIC_FAILURE_REASONS = [
    'not_today',
    'room_update_failed',
    'checkin_update_failed',
    'pcs_dispatch_failed',
    'print_failed',
    'checkin_call_failed',
    'read_card_no_response',
    'patient_lookup_failed',
    'read_card_error',
    'follow_up_no_response',
    'unexpected_error',
    'availability_request_failed',
    'availability_invalid_response',
    'availability_no_calendar',
    'availability_config_incomplete',
    'availability_span_too_long',
    'availability_timeout',
] as const;
export type MetricFailureReason = (typeof METRIC_FAILURE_REASONS)[number];

/** What a call site passes. */
export interface CategoricalAttributes {
    outcome?: MetricOutcome;
    checkin_type?: CheckinType;
    entry?: CheckinEntry;
    availability_mode?: AvailabilityMode;
    availability_action?: AvailabilityAction;
    source?: MetricSource;
    reason?: MetricFailureReason;
}

/**
 * Stamped by the metrics wrapper on every metric, whatever it measures. Not passable by a call site, so
 * a patient or appointment id can never end up in one of these keys.
 */
export interface TenantAttributes {
    customer_id?: string;
    /** The Prismic slug our APIs use, not the backend UUID and not the Prismic document id. */
    location_key?: string;
    /** Sales/demo traffic, per `isSalesEnvironment`. */
    is_test?: boolean;
}

export type AllowedAttributes = CategoricalAttributes & TenantAttributes;

// An in-flight check-in, stamped when handling starts and reused at emission so the duration and
// attribution follow the initiating action (a drag-arrive confirmed via CTA stays `drag`).
export type CheckinAttempt = {
    started_at: number;
    entry: CheckinEntry;
    checkin_type: CheckinType;
};

// How a terminal outcome is labelled: taken from the open attempt, or supplied by a completing caller
// that has none.
export type CheckinAttribution = Pick<CheckinAttempt, 'entry' | 'checkin_type'>;
